import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import Layout from '@/components/Layout';
import { useLanguage } from '@/context/LanguageContext';
import { createTranslator } from '@/i18n';
import {
  Users, UserX, Thermometer, Palmtree, Inbox, AlertTriangle,
  Clock, CalendarDays, FileText, ArrowRight, CircleDot, CheckCircle2,
  Hourglass, MinusCircle, RefreshCw,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const STATUS_META = {
  present: { key: 'dashboard.status_present', Icon: CircleDot, cls: 'bg-green-100 text-green-800 border-green-300' },
  not_arrived: { key: 'dashboard.status_not_arrived', Icon: UserX, cls: 'bg-red-100 text-red-800 border-red-300' },
  sick: { key: 'dashboard.status_sick', Icon: Thermometer, cls: 'bg-amber-100 text-amber-800 border-amber-300' },
  vacation: { key: 'dashboard.status_vacation', Icon: Palmtree, cls: 'bg-blue-100 text-blue-800 border-blue-300' },
  other_absence: { key: 'dashboard.status_other_absence', Icon: FileText, cls: 'bg-purple-100 text-purple-800 border-purple-300' },
  pending_absence: { key: 'dashboard.status_pending_absence', Icon: Hourglass, cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  not_started: { key: 'dashboard.status_not_started', Icon: Clock, cls: 'bg-gray-100 text-gray-600 border-gray-300' },
  finished: { key: 'dashboard.status_finished', Icon: CheckCircle2, cls: 'bg-gray-100 text-gray-700 border-gray-300' },
  off_today: { key: 'dashboard.status_off_today', Icon: MinusCircle, cls: 'bg-gray-50 text-gray-500 border-gray-200' },
};

const LEAVE_LABELS = { sick: 'dashboard.status_sick', vacation: 'dashboard.status_vacation', unpaid_absence: 'dashboard.status_other_absence' };

const fmtWorked = (mins) => {
  if (!mins) return '—';
  return `${Math.floor(mins / 60)}:${String(mins % 60).padStart(2, '0')}`;
};

const StatusBadge = ({ status, t }) => {
  const meta = STATUS_META[status] || STATUS_META.off_today;
  const { Icon } = meta;
  return (
    <Badge variant="outline" className={`${meta.cls} gap-1 font-medium`} data-testid={`status-badge-${status}`}>
      <Icon className="h-3 w-3" />
      {t(meta.key)}
    </Badge>
  );
};

export default function AdminDashboard({ user, onLogout }) {
  const { language: uiLang } = useLanguage();
  const t = createTranslator(uiLang || 'is');
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [upcoming, setUpcoming] = useState(null);
  const [filter, setFilter] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [resolveTarget, setResolveTarget] = useState(null);
  const [resolveReason, setResolveReason] = useState('');
  const [resolving, setResolving] = useState(false);
  const [historyTarget, setHistoryTarget] = useState(null);
  const [historyData, setHistoryData] = useState(null);

  const fetchAll = useCallback(async (silent = true) => {
    if (!silent) setRefreshing(true);
    try {
      const [st, up] = await Promise.all([
        axios.get(`${API}/dashboard/today-status`, authHeaders()),
        axios.get(`${API}/dashboard/upcoming?days=7`, authHeaders()),
      ]);
      setStatus(st.data);
      setUpcoming(up.data);
    } catch (e) {
      console.error('Dashboard fetch error:', e);
    } finally {
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
    const interval = setInterval(fetchAll, 30000);
    const onFocus = () => fetchAll();
    window.addEventListener('focus', onFocus);
    return () => { clearInterval(interval); window.removeEventListener('focus', onFocus); };
  }, [fetchAll]);

  const openHistory = async (r) => {
    setHistoryTarget(r);
    setHistoryData(null);
    try {
      const res = await axios.get(`${API}/attendance/cases/${r.employee_id}/history`, authHeaders());
      setHistoryData(res.data);
    } catch (e) {
      console.error('History fetch error:', e);
      setHistoryData({ cases: [], notifications: [] });
    }
  };

  const submitResolve = async () => {
    if (!resolveTarget || !resolveReason.trim()) return;
    setResolving(true);
    try {
      const caseIds = resolveTarget.review_reasons
        .filter((rr) => rr.type === 'attendance_case')
        .map((rr) => rr.case_id);
      for (const cid of caseIds) {
        await axios.post(`${API}/attendance/cases/${cid}/resolve`, { reason: resolveReason.trim() }, authHeaders());
      }
      toast.success(t('dashboard.resolved_label'));
      setResolveTarget(null);
      setResolveReason('');
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Villa');
    } finally {
      setResolving(false);
    }
  };

  const rows = status?.rows || [];
  const summary = status?.summary || {};

  const filteredRows = rows.filter((r) => {
    if (!filter) return true;
    if (filter === 'present') return r.attendance_status === 'present';
    if (filter === 'not_arrived') return r.attendance_status === 'not_arrived';
    if (filter === 'sick') return r.attendance_status === 'sick';
    if (filter === 'leave') return ['vacation', 'other_absence'].includes(r.attendance_status);
    if (filter === 'review') return r.needs_review;
    return true;
  });

  const actionItems = rows.filter((r) => r.attendance_status === 'not_arrived' || r.needs_review);

  const cards = [
    { id: 'present', label: t('dashboard.present_now'), value: summary.present_now, Icon: Users, cls: 'from-green-50 to-green-100 border-green-200 text-green-700' },
    { id: 'not_arrived', label: t('dashboard.not_arrived'), value: summary.not_arrived, Icon: UserX, cls: summary.not_arrived > 0 ? 'from-red-100 to-red-200 border-red-400 text-red-700 ring-2 ring-red-300' : 'from-gray-50 to-gray-100 border-gray-200 text-gray-600' },
    { id: 'sick', label: t('dashboard.sick_today'), value: summary.sick_today, Icon: Thermometer, cls: 'from-amber-50 to-amber-100 border-amber-200 text-amber-700' },
    { id: 'leave', label: t('dashboard.on_leave'), value: summary.on_leave, Icon: Palmtree, cls: 'from-blue-50 to-blue-100 border-blue-200 text-blue-700' },
    { id: 'pending', label: t('dashboard.pending_requests'), value: summary.pending_requests, Icon: Inbox, cls: 'from-purple-50 to-purple-100 border-purple-200 text-purple-700' },
    { id: 'review', label: t('dashboard.needs_review'), value: summary.needs_review, Icon: AlertTriangle, cls: summary.needs_review > 0 ? 'from-orange-50 to-orange-100 border-orange-300 text-orange-700' : 'from-gray-50 to-gray-100 border-gray-200 text-gray-600' },
  ];

  const handleCardClick = (id) => {
    if (id === 'pending') { navigate('/leave'); return; }
    setFilter(filter === id ? null : id);
  };

  const dayLabel = (iso) => new Date(iso + 'T12:00:00').toLocaleDateString(uiLang === 'en' ? 'en-GB' : 'is-IS', { weekday: 'short', day: 'numeric', month: 'short' });

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="space-y-6" data-testid="admin-dashboard">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-4xl font-bold mb-1" style={{ fontFamily: 'Work Sans' }}>{t('dashboard.title')}</h1>
            <p className="text-gray-600">
              {new Date().toLocaleDateString(uiLang === 'en' ? 'en-GB' : 'is-IS', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={() => fetchAll(false)} disabled={refreshing} data-testid="dashboard-refresh-btn">
            <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            {t('dashboard.refresh')}
          </Button>
        </div>

        {/* Summary cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
          {cards.map(({ id, label, value, Icon, cls }) => (
            <button
              key={id}
              onClick={() => handleCardClick(id)}
              className={`text-left rounded-xl border bg-gradient-to-br p-4 transition-transform hover:scale-[1.02] ${cls} ${filter === id ? 'ring-2 ring-blue-400' : ''}`}
              data-testid={`dashboard-card-${id}`}
            >
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs font-medium">{label}</span>
                <Icon className="h-4 w-4" />
              </div>
              <div className="text-3xl font-bold" data-testid={`dashboard-count-${id}`}>{value ?? '–'}</div>
            </button>
          ))}
        </div>

        {/* Þarf að bregðast við */}
        {actionItems.length > 0 && (
          <Card className="border-red-300 bg-red-50/60 shadow-md" data-testid="action-required-section">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2 text-red-700">
                <AlertTriangle className="h-5 w-5" /> {t('dashboard.action_required')}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {actionItems.map((r) => {
                const cases = r.review_reasons.filter((rr) => rr.type === 'attendance_case');
                const punches = r.review_reasons.filter((rr) => rr.type !== 'attendance_case');
                const latestCase = cases[cases.length - 1];
                const todayIso = status?.date;
                const notArrived = r.attendance_status === 'not_arrived';
                return (
                  <div key={r.employee_id} className="flex flex-wrap items-center justify-between gap-2 bg-white rounded-lg border border-red-200 px-3 py-2" data-testid={`action-item-${r.employee_id}`}>
                    <div>
                      <p className="font-semibold">{r.name}</p>
                      <p className="text-xs text-gray-600">
                        {notArrived && `${t('dashboard.expected_at')} ${r.scheduled_start} · ${t('dashboard.no_clock_in')} · ${t('dashboard.no_absence_registered')}`}
                        {latestCase && (latestCase.date !== todayIso || !notArrived) && (
                          <span>{notArrived ? ' · ' : ''}{t('dashboard.case_unexplained')} {latestCase.date}</span>
                        )}
                        {cases.length > 1 && <span> · +{cases.length - 1} {t('dashboard.older_cases')}</span>}
                        {punches.map((rr, i) => (
                          <span key={`${rr.type}-${rr.date}-${i}`}>
                            {' · '}
                            {rr.type === 'missing_clock_out' ? `${t('dashboard.missing_clock_out')} ${rr.date}` : t('dashboard.overlapping_entries')}
                          </span>
                        ))}
                      </p>
                      {latestCase?.notified_at && (
                        <p className="text-xs text-gray-500">
                          {t('dashboard.last_notification')}: {latestCase.notified_at.slice(0, 10)} {latestCase.notified_at.slice(11, 16)}
                          {' · '}
                          {latestCase.notification_status === 'pending' || !latestCase.notification_status
                            ? t('dashboard.notification_no_answer') : latestCase.notification_status}
                        </p>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <Button size="sm" variant="outline" onClick={() => navigate(`/leave?new=1&user_id=${r.user_id}&date=${latestCase?.date || todayIso}`)} data-testid={`action-register-absence-${r.employee_id}`}>
                        {t('dashboard.register_absence')}
                      </Button>
                      {cases.length > 0 && (
                        <Button size="sm" variant="outline" onClick={() => { setResolveTarget(r); setResolveReason(''); }} data-testid={`action-mark-resolved-${r.employee_id}`}>
                          {t('dashboard.mark_resolved')}
                        </Button>
                      )}
                      <Button size="sm" variant="ghost" onClick={() => openHistory(r)} data-testid={`action-history-${r.employee_id}`}>
                        {t('dashboard.see_history')}
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => (punches.length > 0 || cases.length === 0 ? navigate('/timesheet') : openHistory(r))} data-testid={`action-open-${r.employee_id}`}>
                        {t('dashboard.open')}
                      </Button>
                    </div>
                  </div>
                );
              })}
            </CardContent>
          </Card>
        )}

        {/* Staðan í dag */}
        <Card className="shadow-md" data-testid="today-status-card">
          <CardHeader className="pb-2 flex flex-row items-center justify-between">
            <CardTitle className="text-lg">{t('dashboard.today_status')}</CardTitle>
            {filter && (
              <Button variant="ghost" size="sm" onClick={() => setFilter(null)} data-testid="clear-filter-btn">
                {t('dashboard.clear_filter')} ✕
              </Button>
            )}
          </CardHeader>
          <CardContent>
            {filteredRows.length === 0 ? (
              <p className="text-sm text-gray-500 py-4">{t('dashboard.no_employees')}</p>
            ) : (
              <>
                {/* Desktop table */}
                <table className="hidden md:table w-full text-sm" data-testid="today-status-table">
                  <thead>
                    <tr className="text-left text-xs text-gray-500 border-b">
                      <th className="py-2 pr-2">{t('dashboard.employee')}</th>
                      <th className="py-2 pr-2">{t('dashboard.shift')}</th>
                      <th className="py-2 pr-2">{t('dashboard.status')}</th>
                      <th className="py-2 pr-2">{t('dashboard.arrival')}</th>
                      <th className="py-2 pr-2">{t('dashboard.worked_so_far')}</th>
                      <th className="py-2"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRows.map((r) => (
                      <tr key={r.employee_id} className="border-b last:border-0 hover:bg-gray-50" data-testid={`today-row-${r.employee_id}`}>
                        <td className="py-2 pr-2 font-medium">
                          {r.name}
                          {r.needs_review && (
                            <Badge variant="outline" className="ml-2 bg-orange-50 text-orange-700 border-orange-300 text-[10px]">
                              {t('dashboard.needs_review')}
                            </Badge>
                          )}
                        </td>
                        <td className="py-2 pr-2 text-gray-600">
                          {r.scheduled_start ? `${r.scheduled_start}–${r.scheduled_end}` : '—'}
                        </td>
                        <td className="py-2 pr-2"><StatusBadge status={r.attendance_status} t={t} /></td>
                        <td className="py-2 pr-2">{r.first_clock_in_time || '—'}</td>
                        <td className="py-2 pr-2">{fmtWorked(r.worked_minutes_today)}</td>
                        <td className="py-2 text-right">
                          <Button size="sm" variant="ghost" onClick={() => navigate('/timesheet')} data-testid={`open-employee-${r.employee_id}`}>
                            {t('dashboard.open')} <ArrowRight className="h-3 w-3 ml-1" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {/* Mobile cards */}
                <div className="md:hidden space-y-2">
                  {filteredRows.map((r) => (
                    <div key={r.employee_id} className="border rounded-lg p-3 flex items-center justify-between gap-2" data-testid={`today-card-${r.employee_id}`}>
                      <div>
                        <p className="font-medium">{r.name}</p>
                        <p className="text-xs text-gray-500">
                          {r.scheduled_start ? `${r.scheduled_start}–${r.scheduled_end}` : '—'}
                          {r.first_clock_in_time && ` · ${t('dashboard.arrival')} ${r.first_clock_in_time}`}
                          {r.worked_minutes_today > 0 && ` · ${fmtWorked(r.worked_minutes_today)}`}
                        </p>
                      </div>
                      <StatusBadge status={r.attendance_status} t={t} />
                    </div>
                  ))}
                </div>
              </>
            )}
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Biðandi beiðnir */}
          <Card className="shadow-md" data-testid="pending-requests-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <Inbox className="h-4 w-4" /> {t('dashboard.pending_requests')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {summary.pending_requests > 0 ? (
                <div className="space-y-2">
                  {Object.entries(status?.pending_by_type || {}).map(([type, count]) => (
                    <div key={type} className="flex justify-between items-center text-sm" data-testid={`pending-type-${type}`}>
                      <span>{t(LEAVE_LABELS[type] || 'dashboard.status_other_absence')}</span>
                      <Badge variant="secondary">{count}</Badge>
                    </div>
                  ))}
                  <Button variant="outline" size="sm" className="w-full mt-2" onClick={() => navigate('/leave')} data-testid="view-all-requests-btn">
                    {t('dashboard.view_all_requests')}
                  </Button>
                </div>
              ) : (
                <p className="text-sm text-gray-500" data-testid="no-pending-requests">{t('dashboard.no_pending')}</p>
              )}
            </CardContent>
          </Card>

          {/* Quick actions */}
          <Card className="shadow-md" data-testid="quick-actions-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg">{t('dashboard.quick_actions')}</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-2 gap-2">
              <Button variant="outline" onClick={() => navigate('/leave')} data-testid="qa-register-absence">
                <FileText className="h-4 w-4 mr-2" /> {t('dashboard.register_absence')}
              </Button>
              <Button variant="outline" onClick={() => navigate('/leave')} data-testid="qa-view-requests">
                <Inbox className="h-4 w-4 mr-2" /> {t('dashboard.view_requests')}
              </Button>
              <Button variant="outline" onClick={() => navigate('/timesheet')} data-testid="qa-open-timesheet">
                <Clock className="h-4 w-4 mr-2" /> {t('dashboard.open_timesheet')}
              </Button>
              <Button variant="outline" onClick={() => navigate('/calendar')} data-testid="qa-open-calendar">
                <CalendarDays className="h-4 w-4 mr-2" /> {t('dashboard.open_calendar')}
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Næstu 7 dagar */}
        <Card className="shadow-md" data-testid="next-7-days-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <CalendarDays className="h-4 w-4" /> {t('dashboard.next_7_days')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-2">
              {(upcoming?.days || []).map((d) => (
                <button
                  key={d.date}
                  onClick={() => navigate('/calendar')}
                  className="text-left border rounded-lg p-2 hover:bg-gray-50 transition-colors"
                  data-testid={`upcoming-day-${d.date}`}
                >
                  <p className="text-xs font-semibold capitalize">{dayLabel(d.date)}</p>
                  <p className="text-[10px] text-gray-400 mb-1">{d.scheduled_count} {t('dashboard.scheduled_short')}</p>
                  {d.absences.length === 0 ? (
                    <p className="text-[11px] text-gray-400">{t('dashboard.no_absences')}</p>
                  ) : (
                    d.absences.slice(0, 3).map((a, i) => (
                      <p key={i} className="text-[11px] truncate">
                        <span className={a.leave_type === 'sick' ? 'text-amber-700' : a.leave_type === 'vacation' ? 'text-blue-700' : 'text-purple-700'}>
                          {a.name.split(' ')[0]} – {t(LEAVE_LABELS[a.leave_type] || 'dashboard.status_other_absence')}
                          {a.status === 'pending' ? ' (?)' : ''}
                        </span>
                      </p>
                    ))
                  )}
                  {d.absences.length > 3 && <p className="text-[10px] text-gray-400">+{d.absences.length - 3}</p>}
                </button>
              ))}
            </div>
          </CardContent>
        </Card>
        {/* Merkja afgreitt dialog */}
        <Dialog open={!!resolveTarget} onOpenChange={(o) => !o && setResolveTarget(null)}>
          <DialogContent data-testid="resolve-case-dialog">
            <DialogHeader>
              <DialogTitle>{t('dashboard.resolve_dialog_title')}</DialogTitle>
              <DialogDescription>{t('dashboard.resolve_dialog_desc')}</DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <p className="text-sm font-medium">{resolveTarget?.name}</p>
              <div className="space-y-1">
                <Label htmlFor="resolve-reason">{t('dashboard.resolve_reason_label')}</Label>
                <Textarea
                  id="resolve-reason"
                  value={resolveReason}
                  onChange={(e) => setResolveReason(e.target.value)}
                  placeholder={t('dashboard.resolve_reason_placeholder')}
                  data-testid="resolve-reason-input"
                />
              </div>
              <div className="flex justify-end gap-2 pt-1">
                <Button variant="outline" onClick={() => setResolveTarget(null)} data-testid="resolve-case-cancel">
                  {t('dashboard.cancel')}
                </Button>
                <Button onClick={submitResolve} disabled={!resolveReason.trim() || resolving} data-testid="resolve-case-submit">
                  {t('dashboard.mark_resolved')}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Saga dialog */}
        <Dialog open={!!historyTarget} onOpenChange={(o) => !o && setHistoryTarget(null)}>
          <DialogContent className="max-h-[80vh] overflow-y-auto" data-testid="case-history-dialog">
            <DialogHeader>
              <DialogTitle>{t('dashboard.history_title')} – {historyTarget?.name}</DialogTitle>
            </DialogHeader>
            {!historyData ? (
              <p className="text-sm text-gray-500">…</p>
            ) : (
              <div className="space-y-4">
                <div>
                  <p className="text-sm font-semibold mb-1">{t('dashboard.history_cases')}</p>
                  {historyData.cases.length === 0 ? (
                    <p className="text-xs text-gray-500">{t('dashboard.no_history')}</p>
                  ) : (
                    <div className="space-y-1">
                      {historyData.cases.map((c) => (
                        <div key={c.id} className="text-xs flex flex-wrap items-center gap-2 border rounded px-2 py-1" data-testid={`history-case-${c.id}`}>
                          <span className="font-medium">{c.date}</span>
                          <Badge variant="outline" className={c.status === 'unresolved' ? 'bg-red-50 text-red-700 border-red-200' : 'bg-green-50 text-green-700 border-green-200'}>
                            {c.status === 'unresolved' ? t('dashboard.unresolved_label') : t('dashboard.resolved_label')}
                          </Badge>
                          {c.resolution_type && <span className="text-gray-500">{c.resolution_type}</span>}
                          {c.resolved_by && <span className="text-gray-400">{c.resolved_by}</span>}
                          {c.reason && <span className="text-gray-500 italic">„{c.reason}“</span>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div>
                  <p className="text-sm font-semibold mb-1">{t('dashboard.history_notifications')}</p>
                  {historyData.notifications.length === 0 ? (
                    <p className="text-xs text-gray-500">{t('dashboard.no_history')}</p>
                  ) : (
                    <div className="space-y-1">
                      {historyData.notifications.map((n) => (
                        <div key={n.id} className="text-xs flex flex-wrap items-center gap-2 border rounded px-2 py-1">
                          <span className="font-medium">{n.shift_date}</span>
                          <span className="text-gray-500">{(n.sent_at || '').slice(11, 16)}</span>
                          <span className="text-gray-500">{n.delivery_status}</span>
                          <Badge variant="outline">{n.response_status}</Badge>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
